import data from './aide-connexion.json';

/**
 * Aide à la connexion : SOURCE UNIQUE du texte de la page publique
 * /aide/connexion ET du PDF /aide/aide-connexion.pdf.
 *
 * Le texte vit dans aide-connexion.json. Après toute modification :
 *   npm --workspace web run aide:pdf
 * qui régénère le PDF (web/public/aide/) et l'empreinte
 * aide-connexion.pdf.sha256. Un test échoue tant que le PDF n'a pas été
 * régénéré (empreinte du JSON différente).
 *
 * Page publique : aucune information sensible (ni adresse interne, ni nom de
 * machine, ni compte de secours) ; un test le vérifie.
 */

export type AideBloc =
  | { type: 'h3'; texte: string }
  | { type: 'p'; texte: string }
  | { type: 'note'; texte: string }
  | { type: 'etapes'; items: string[] }
  | { type: 'liste'; items: string[] };

export interface AideSection {
  id: string;
  titre: string;
  blocs: AideBloc[];
}

export interface AideContenu {
  titre: string;
  produit: string;
  miseAJour: string;
  intro: string;
  sections: AideSection[];
}

/**
 * Typographie française : espace insécable après « et avant », : ; ? ! pour
 * qu'un guillemet ou un signe ne se retrouve pas seul en début de ligne.
 * Même règle dans scripts/aide-connexion-pdf.mjs.
 */
export function insecables(s: string): string {
  return s.replace(/« /g, '«\u00a0').replace(/ ([»:;?!])/g, '\u00a0$1');
}

function typo<T>(v: T): T {
  if (typeof v === 'string') return insecables(v) as T;
  if (Array.isArray(v)) return v.map(typo) as T;
  if (v && typeof v === 'object') {
    return Object.fromEntries(Object.entries(v).map(([k, x]) => [k, k === 'id' || k === 'type' ? x : typo(x)])) as T;
  }
  return v;
}

export const AIDE_CONNEXION: AideContenu = typo(data as AideContenu);

/** Chemin public du PDF (servi en statique depuis web/public). */
export const AIDE_CONNEXION_PDF = '/aide/aide-connexion.pdf';

/** Nom proposé au téléchargement. */
export const AIDE_CONNEXION_PDF_NOM = 'Aide-connexion-Jarvis-Support.pdf';

/** Découpe un texte en morceaux, les adresses e-mail à part (liens mailto). */
export function splitEmails(texte: string): { texte: string; mail: boolean }[] {
  const out: { texte: string; mail: boolean }[] = [];
  const re = /[A-Za-z0-9._-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
  let last = 0;
  for (const m of texte.matchAll(re)) {
    const i = m.index ?? 0;
    if (i > last) out.push({ texte: texte.slice(last, i), mail: false });
    out.push({ texte: m[0], mail: true });
    last = i + m[0].length;
  }
  if (last < texte.length) out.push({ texte: texte.slice(last), mail: false });
  return out;
}
